import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip, Avatar, Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  ContentCopy,
  Delete,
  ArrowForwardIos,
  AccessTime,
  Image as ImageIcon,
  MicNone,
  Videocam as VideoIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import { FiSend } from "react-icons/fi";
import { MdTag, MdMessage } from "react-icons/md";
import { useNodeStorage } from "../../../stores/useNodeStorage";

function ActiveMessageNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const renderElementPreview = (element) => {
    switch (element.type) {
      case "message":
        return (
          <div style={{ gap: theme.spacing(1) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              <MdMessage
                style={{
                  color: theme.palette.primary.main,
                  width: "16px",
                  height: "16px",
                }}
              />
              <div
                style={{
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Texto
              </div>
            </div>
            <Typography
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                fontSize: "12px",
                lineHeight: 1.5,
                marginTop: 4,
              }}
            >
              {truncateText(element.value, 50)}
            </Typography>
          </div>
        );

      case "img":
        return (
          <div style={{ gap: theme.spacing(1) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              <ImageIcon
                style={{
                  color: theme.palette.primary.main,
                  width: "16px",
                  height: "16px",
                }}
              />
              <div
                style={{
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Imagem
              </div>
            </div>
            <Typography
              variant="caption"
              align="center"
              style={{
                marginTop: "4px",
                display: "block",
                fontSize: "10px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={element.value}
            >
              {element.value}
            </Typography>
          </div>
        );

      case "video":
        return (
          <div style={{ gap: theme.spacing(1) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              <VideoIcon
                style={{
                  color: theme.palette.primary.main,
                  width: "16px",
                  height: "16px",
                }}
              />
              <div
                style={{
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Video
              </div>
            </div>
            <Typography
              variant="caption"
              align="center"
              style={{
                marginTop: "4px",
                display: "block",
                fontSize: "10px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={element.value}
            >
              {element.value}
            </Typography>
          </div>
        );

      case "audio":
        return (
          <div style={{ gap: theme.spacing(1) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              <MicNone
                style={{
                  color: theme.palette.primary.main,
                  width: "16px",
                  height: "16px",
                }}
              />
              <div
                style={{
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Audio
              </div>
            </div>
            <Typography
              variant="caption"
              style={{
                color: theme.palette.text.secondary,
                fontSize: "10px",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={element.value}
            >
              {element.value}
            </Typography>
          </div>
        );

      case "interval":
        return (
          <div style={{ gap: theme.spacing(1) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              <AccessTime
                style={{
                  color: theme.palette.primary.main,
                  width: "16px",
                  height: "16px",
                }}
              />
              <div
                style={{
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Intervalo
              </div>
            </div>
            <Typography
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                fontSize: "12px",
                lineHeight: 1.5,
                marginTop: 4,
              }}
            >
              {element.value} segundos
            </Typography>
          </div>
        );

      case "file":
        return (
          <div style={{ gap: theme.spacing(1) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              <FileIcon
                style={{
                  color: theme.palette.primary.main,
                  width: "16px",
                  height: "16px",
                }}
              />
              <div
                style={{
                  color: theme.palette.primary.main,
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Arquivo
              </div>
            </div>
            <Typography
              variant="caption"
              style={{
                color: theme.palette.text.secondary,
                fontSize: "10px",
                marginTop: 4,
                textAlign: "center",
              }}
            >
              {element.original || element.value}
            </Typography>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `2px solid #4caf50`,
        boxShadow: `0 4px 8px rgba(0, 0, 0, 0.15)`,
        position: "relative",
        transition: "all 0.2s ease",
        width: "auto",
        minWidth: "220px",
        maxWidth: "260px",
        boxSizing: "border-box",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          left: "-14px",
          top: "20px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #4caf50 0%, #388e3c 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botoes de acao */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: theme.spacing(1),
          top: theme.spacing(1),
          gap: theme.spacing(1),
          cursor: "pointer",
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1.5),
        }}
      >
        <FiSend
          style={{
            width: "18px",
            height: "18px",
            color: "#4caf50",
          }}
        />
        <div
          style={{
            color: theme.palette.text.primary,
            fontSize: "15px",
            fontWeight: 500,
          }}
        >
          Mensagem Ativa
        </div>
      </div>

      {/* Chip com contagem de numeros ou grupo */}
      <Chip
        size="small"
        variant="outlined"
        label={
          data.groupName
            ? `Grupo: ${data.groupName}`
            : `${data.phoneNumbers?.length || 0} numeros`
        }
        style={{
          marginBottom: theme.spacing(2),
          backgroundColor: "rgba(76, 175, 80, 0.2)",
          borderColor: "#4caf50",
          border: "1px solid #4caf50",
          color: "#4caf50",
          fontWeight: 500,
        }}
      />

      {/* Tags selecionadas */}
      {data.tagIds && data.tagIds.length > 0 && (
        <Box
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginBottom: 16,
            width: "100%",
            flexDirection: "column",
            alignItems: "flex-start",
            paddingRight: 0,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            paddingBottom: "12px",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              marginBottom: "6px",
            }}
          >
            <MdTag
              style={{
                width: "16px",
                height: "16px",
                color: theme.palette.primary.main,
                marginRight: "4px",
              }}
            />
            <Typography
              variant="caption"
              style={{ fontWeight: 500, color: theme.palette.primary.main }}
            >
              Tags selecionadas
            </Typography>
          </Box>

          <Box
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              width: "100%",
              backgroundColor: "rgba(63, 81, 181, 0.08)",
              padding: "6px",
              borderRadius: "4px",
              border: "1px solid rgba(63, 81, 181, 0.5)",
            }}
          >
            {(data.tags || []).map((tag) => (
              <Chip
                key={tag.id}
                size="small"
                label={truncateText(tag.name, 12)}
                title={tag.name}
                style={{
                  backgroundColor: tag.color || "#eee",
                  color: "#FFF",
                  fontSize: "0.7rem",
                  height: "20px",
                  margin: "0 2px 2px 0",
                  maxWidth: "100%",
                  overflow: "hidden",
                  fontWeight: 500,
                }}
              />
            ))}

            {!data.tags && data.tagIds?.length > 0 && (
              <Chip
                size="small"
                label={`${data.tagIds.length} tag${
                  data.tagIds.length > 1 ? "s" : ""
                }`}
                style={{
                  backgroundColor: theme.palette.primary.light,
                  color: "#FFF",
                  fontSize: "0.7rem",
                  height: "20px",
                  margin: "0 2px 2px 0",
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Elementos da mensagem */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
          boxSizing: "border-box",
          padding: 0,
          marginTop: "12px",
        }}
      >
        {data.seq &&
          data.seq.slice(0, 3).map((item) => {
            const element = data.elements?.find((el) => el.number === item);
            return element ? (
              <div
                key={item}
                style={{
                  backgroundColor: theme.palette.background.default,
                  borderRadius: theme.shape.borderRadius,
                  padding: theme.spacing(2),
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid rgba(0, 0, 0, 0.15)",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
              >
                {renderElementPreview(element)}
              </div>
            ) : null;
          })}
        {data.seq && data.seq.length > 3 && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary, textAlign: "center" }}
          >
            +{data.seq.length - 3} mais elemento(s)
          </Typography>
        )}
      </div>

      {/* Handle de saida */}
      <Handle
        type="source"
        position={Position.Right}
        id="a"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #4caf50 0%, #388e3c 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(ActiveMessageNode);
